/**
 * «Siamo dentro l'app?» — la domanda che precede ogni funzione nativa.
 *
 * ⚠️ Fino al 09-09-2026 la risposta si leggeva dal guscio **precedente**, ed era **sbagliata
 * nell'unico posto che conta**: l'APK si impacchetta con Cordova, dove quell'oggetto non esiste.
 * Ogni funzione che chiedeva «siamo dentro l'app?» riceveva «no» dentro l'app. Non dava errori —
 * ogni modulo prendeva il suo ramo web e taceva — e ha prodotto una fila di difetti che si
 * vedevano solo su un telefono: la fotocamera nativa, il salvataggio dei file, le notifiche push,
 * la coda delle timbrature sul `localStorage` nudo, la scrittura NFC.
 *
 * Il valore si legge **a ogni chiamata** e non si memorizza: nei test la piattaforma viene finta
 * e disfatta di continuo, e una costante calcolata all'import renderebbe verde il primo test e
 * rosso il secondo, per un motivo che non si vede leggendo il test.
 */

export type Platform = 'android' | 'ios' | 'web';

export function platform(): Platform {
  if (typeof window === 'undefined') return 'web';
  /* ⚠️ `platformId` vale `'browser'` quando Cordova gira in `cordova serve`: li' le API native
     non ci sono, ed e' web a tutti gli effetti. Va trattato come web, non come «Cordova». */
  const id = (window as { cordova?: { platformId?: string } }).cordova?.platformId;
  return id === 'android' || id === 'ios' ? id : 'web';
}

/** Dentro l'app (Android o iOS). Sul web — e in Node — è falso. */
export function isNative(): boolean {
  return platform() !== 'web';
}

/**
 * **Quale** guscio nativo ci sta ospitando.
 *
 * ⚠️ Sono rimasti **due** valori: ce n'era un terzo per il guscio precedente, e questa funzione
 * era nata proprio per distinguerli — il repo portava due gusci, e i moduli nativi avevano due
 * rami. Quel secondo progetto e' stato rimosso il 09-09-2026: era a `targetSdk 34`, quindi non si
 * aggiornava nemmeno sul Play Store dal 31-08-2025, e i suoi rami nell'APK non venivano mai
 * eseguiti.
 *
 * ⚠️ Resta distinta da `piattaforma()` **di proposito**, anche se oggi le due risposte si
 * ricavano dalla stessa riga. Le domande sono diverse: `piattaforma()` chiede «Android o iOS»,
 * questa chiede «c'e' un guscio nativo che puo' avere plugin». Fonderle vorrebbe dire che il
 * giorno che si aggiunge un secondo guscio bisogna ritrovare tutti i chiamanti e capire quale
 * delle due cose intendevano — che e' esattamente il lavoro che questa distinzione ha risparmiato
 * quando il guscio vivo era l'altro.
 */
export type NativeRuntime = 'cordova' | 'web';

/**
 * L'app costruita per lo **sviluppo**: parla col sistema di sviluppo. App di sviluppo e di
 * produzione hanno lo stesso identificativo e si sostituiscono sul telefono, quindi la home lo
 * dice con un badge. Sul web no: lì l'indirizzo lo dice già.
 *
 * ⚠️ `MODE` e non `import.meta.env.DEV`: `vite build --mode development` costruisce con `DEV`
 * falso, perché `DEV` dice «server di sviluppo di Vite», non «per quale sistema».
 */
export function isDevelopmentApp(mode: string = import.meta.env.MODE): boolean {
  return isNative() && mode === 'development';
}

export function nativeRuntime(): NativeRuntime {
  return platform() === 'web' ? 'web' : 'cordova';
}

/*
 * ⚠️ Qui c'erano `versioneAndroid()`, `ANDROID_MIN_NFC = 13` e `nfcBloccatoDaVersione()`, e sono
 * andati via col plugin che li rendeva necessari.
 *
 * Il vincolo era del plugin NFC del guscio precedente, che leggeva il tag con
 * `intent.getParcelableExtra(EXTRA_TAG, Tag::class.java)` sotto un `@RequiresApi(TIRAMISU)`:
 * quell'overload esiste **da API 33**, e sotto quella versione appoggiare un tag non falliva —
 * *terminava l'app*. Il cancello serviva a non far nemmeno provare.
 *
 * `community-cordova-plugin-nfc` usa l'overload **deprecato** a un argomento, senza nessun
 * `@RequiresApi` e senza controlli su `SDK_INT` oltre a JELLY_BEAN e M. Verificato leggendo
 * `NfcPlugin.java`, non dedotto dal fatto che «e' un altro plugin».
 *
 * Quindi il cancello non e' stato semplicemente rimosso come codice inutile: **teneva chiuso
 * qualcosa che funziona**. Con `minSdkVersion 24`, l'NFC ora e' disponibile anche su Android da 7
 * a 12, dove prima l'app diceva «richiede Android 13» a chi ce l'aveva perfettamente utilizzabile.
 */

/**
 * Una descrizione del dispositivo buona per l'audit del BE (`deviceInfo` su `erp-attendances`).
 *
 * Volutamente povera: piattaforma e user agent troncato. Non è telemetria — serve a distinguere
 * «timbrato dall'app Android» da «timbrato dal browser del portatile» quando qualcuno, mesi dopo,
 * contesta una riga.
 */
export function deviceDescription(): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return `${platform()}${ua ? ` · ${ua.slice(0, 120)}` : ''}`;
}

/**
 * Il costruttore `File` **del browser**, non quello che il plugin ci ha messo sopra.
 *
 * ⚠️ `cordova-plugin-file` fa `clobbers` su **undici** nomi globali — fra cui `File`, `FileReader`,
 * `Entry`, `ProgressEvent`, `Metadata` — e il suo `File` e' un'altra cosa:
 * `File(name, localURL, type, lastModifiedDate, size)`, un contenitore di metadati senza byte.
 * Quindi dentro l'APK `new File([blob], nome, { type })` **non costruisce un file**: mette il blob
 * nel campo `name` e l'oggetto delle opzioni in `type`. Cio' che ne esce non e' un `Blob`, quindi
 * `URL.createObjectURL` lo rifiuta e React, se prova a disegnarne il nome, muore con l'errore 31.
 *
 * Fuori dall'app non c'e' niente da recuperare e si restituisce `File`: e' lo stesso oggetto.
 * Dentro, Cordova conserva gli originali in `window.CDV_origSymbols` — e' da la' che
 * `modulemapper.getOriginalSymbol` li legge — e questa e' la via documentata per riprenderli.
 *
 * ⚠️ Il `FileReader` invece si lascia **quello del plugin**: e' l'unico che sa leggere le sue voci
 * di filesystem, e sui `Blob` veri delega da se' a quello del browser.
 *
 * Il difetto e' invisibile a ogni prova: nel finto browser dei test `File` e' quello vero, quindi
 * il codice sbagliato passa. Si vede solo su un telefono, e si vede come schermata di errore dopo
 * un gesto **riuscito**.
 */
type WindowWithOriginalSymbols = Window & { CDV_origSymbols?: Record<string, unknown> };

/**
 * ⚠️ La chiave e' `'window.File'`, **non** `'File'`, e la differenza e' costata un giro sul
 * telefono. `origSymbols` e' indicizzato con il `target` scritto nel `plugin.xml`, che passa in
 * `cordova_plugins.js` cosi' com'e': `"window.File"`. Cercando `'File'` si ottiene `undefined` e
 * si ricade sul costruttore del plugin — cioe' esattamente il difetto che questa funzione esiste
 * per evitare, con l'aggravante di sembrare risolto.
 *
 * Si prova anche `'File'`: costa niente e copre il caso di un contesto dichiarato senza prefisso.
 * L'ordine conta, perche' la prima chiave che risponde vince.
 */
const FILE_KEYS = ['window.File', 'File'] as const;

export function browserFileConstructor(): typeof File {
  if (typeof window === 'undefined') return File;
  const original = (window as WindowWithOriginalSymbols).CDV_origSymbols;
  for (const key of FILE_KEYS) {
    const candidate = original?.[key];
    if (typeof candidate === 'function') return candidate as typeof File;
  }
  return File;
}


/**
 * UN INDIRIZZO FUORI DALL'APPLICAZIONE — la porta unica per aprirlo.
 *
 * Esiste per MaeSpace (`docs/FE-TO-BE-MAESPACE-CONTROPROPOSTA-v1.md` §1.1), ma la domanda e' di
 * chiunque debba portare l'utente su un altro sito, e le due trappole sono una per piattaforma.
 *
 * **Web.** Un `window.open` dopo un `await` non e' piu' «dentro il gesto dell'utente», e Safari (cioe'
 * ogni browser su iPhone) lo blocca senza dirlo. Quindi la finestra si apre **subito**, al clic, con
 * `openExternalWindow()`, e le si da' l'indirizzo quando arriva. Niente `'noopener'` nella chiamata:
 * con quella bandiera `window.open` restituisce `null` e la finestra non si puo' piu' indirizzare; il
 * legame col chiamante si taglia a mano, con `opener = null`, che e' lo stesso effetto.
 *
 * **App (Cordova).** `APP/config.xml` ha `<allow-navigation href="*" />`, che vince su
 * `allow-intent`: un link o un `window.open` verso un altro sito puo' caricarsi **dentro** la WebView,
 * al posto dell'applicazione — e una videochiamata li' dentro non ha camera ne' microfono. Sul
 * nativo si chiede a Cordova di passare l'indirizzo al sistema (`navigator.app.loadUrl` con
 * `openExternal`), che apre il browser vero. Non c'e' niente da preparare prima, quindi la finestra
 * di attesa li' non serve: `openExternalWindow()` restituisce una maniglia che fa solo questo.
 */
export interface ExternalWindow {
  /** Porta la finestra all'indirizzo (o, nell'app, lo passa al browser di sistema). */
  go(url: string): void;
  /** La chiude se non serve piu' (la richiesta e' fallita). Nell'app non c'e' niente da chiudere. */
  close(): void;
}

type CordovaNavigator = Navigator & {
  app?: { loadUrl?: (url: string, props?: { openExternal?: boolean }) => void };
};

function openWithSystem(url: string): void {
  const nav = (typeof navigator === 'undefined' ? undefined : navigator) as CordovaNavigator | undefined;
  if (nav?.app?.loadUrl) {
    nav.app.loadUrl(url, { openExternal: true });
    return;
  }
  /* iOS, o un guscio senza `navigator.app`: `_system` e' la convenzione di Cordova per «fuori». */
  window.open(url, '_system');
}

/**
 * Apre **subito** la finestra che ricevera' l'indirizzo. Va chiamata nel gestore del clic, prima di
 * qualunque `await`. Restituisce `null` se il browser l'ha bloccata comunque.
 */
export function openExternalWindow(waitingText = 'Apertura in corso…'): ExternalWindow | null {
  if (isNative()) return { go: openWithSystem, close: () => {} };
  const w = window.open('', '_blank');
  if (!w) return null;
  try {
    w.opener = null;
    w.document.title = waitingText;
    w.document.body.style.font = '15px system-ui, sans-serif';
    w.document.body.style.padding = '32px';
    w.document.body.textContent = waitingText;
  } catch {
    /* Una finestra che non si lascia scrivere si puo' comunque indirizzare. */
  }
  return {
    go: (url) => { w.location.href = url; },
    close: () => { w.close(); },
  };
}

/** Un indirizzo che si ha gia' in mano: si apre fuori dall'applicazione, nella scheda nuova o nel browser di sistema. */
export function openExternalUrl(url: string): boolean {
  const w = openExternalWindow();
  if (!w) return false;
  w.go(url);
  return true;
}
